/**
 * reCAPTCHA Enterprise helpers for explicit actions (e.g. signup).
 * App Check also uses Enterprise; this requests an action-bound token
 * that Cloud Functions assess server-side for score + action.
 */

import { logger } from './logger';

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

/**
 * Ensure grecaptcha.enterprise is available (loads script if needed).
 * @returns {Promise<void>}
 */
export function ensureRecaptchaEnterprise() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA is only available in the browser'));
  }

  if (!SITE_KEY) {
    return Promise.reject(new Error('VITE_RECAPTCHA_SITE_KEY is not configured'));
  }

  if (window.grecaptcha?.enterprise) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="recaptcha/enterprise.js"]');
    const onReady = () => {
      if (window.grecaptcha?.enterprise) {
        resolve();
      } else {
        reject(new Error('reCAPTCHA Enterprise failed to load'));
      }
    };

    if (existing) {
      // Script tag present; wait briefly for globals
      let attempts = 0;
      const poll = setInterval(() => {
        attempts += 1;
        if (window.grecaptcha?.enterprise) {
          clearInterval(poll);
          resolve();
        } else if (attempts > 40) {
          clearInterval(poll);
          reject(new Error('reCAPTCHA Enterprise failed to load'));
        }
      }, 100);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = onReady;
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
    document.head.appendChild(script);
  });
}

/**
 * Execute reCAPTCHA Enterprise for a named action.
 * @param {string} action
 * @returns {Promise<string>} token
 */
export async function getRecaptchaToken(action = 'signup') {
  await ensureRecaptchaEnterprise();

  return new Promise((resolve, reject) => {
    try {
      window.grecaptcha.enterprise.ready(async () => {
        try {
          const token = await window.grecaptcha.enterprise.execute(SITE_KEY, { action });
          if (!token) {
            reject(new Error('Empty reCAPTCHA token'));
            return;
          }
          resolve(token);
        } catch (err) {
          logger.error('[recaptcha] execute failed', err);
          reject(err);
        }
      });
    } catch (err) {
      reject(err);
    }
  });
}
